import SlideoverModal from "src/components/SlideoverModal";
import React, { useEffect, useState } from "react";
import LoadingIcon from "src/base-components/LoadingIcon";
import Lucide from "src/base-components/Lucide";
import PostCallRemarks from "./PostCallRemarks";
import { selectAuthUser } from "src/stores/auth";
import { useAppSelector } from "src/stores";
import { userTimeZone } from "src/components/CallsLogsModal/constants";
import { Get } from "src/services/http/fetch";
import { Pagination, QueueCallItem, QueueCallsState } from "src/interfaces";

interface ComponentProps {
  visible: boolean;
  setVisible: (payload: boolean) => void;
  getQueueCalls: QueueCallsState;
}

const PendingRemarksPanel: React.FC<ComponentProps> = ({
  visible,
  setVisible,
  getQueueCalls,
}) => {
  const [openPostCall, setOpenPostCall] = useState(false);
  const [getCallerNum, setGetCallerNum] = useState<string | null>(null);
  const [direction, setDirection] = useState<string | null>(null);
  const [getSessionID, setGetSessionID] = useState<string | null>(null);

  const [list, setList] = useState<QueueCallItem[]>([]);
  const [pagination, setPagination] = useState<Pagination | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const authUser = useAppSelector(selectAuthUser);

  const handleClose = () => {
    setOpenPostCall(false);
    setGetCallerNum(null);
    setGetSessionID(null);
    setDirection(null);
  };

  useEffect(() => {
    if (visible && getQueueCalls?.data?.data) {
      setList(getQueueCalls.data.data);
      setPagination(getQueueCalls.data.pagination ?? null);
    }
  }, [visible, getQueueCalls?.data]);

  const loadMore = async () => {
    if (loadingMore || !pagination) return;
    if (pagination.current_page >= pagination.last_page) return;

    setLoadingMore(true);
    try {
      const nextPage = pagination.current_page + 1;
      const res: any = await Get(`/queue-calls?page=${nextPage}`);

      if (res?.success && Array.isArray(res.data)) {
        const newData: QueueCallItem[] = res.data;

        setList((prev) => [...prev, ...newData]); // append next page
        if (res.pagination) {
          setPagination(res.pagination);
        }
      } else {
        setPagination((prev) =>
          prev ? { ...prev, current_page: prev.last_page } : null,
        );
      }
    } catch (err) {
      console.error("Error loading more queue calls:", err);
    } finally {
      setLoadingMore(false);
    }
  };

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const el = e.currentTarget;

    if (el.scrollHeight <= el.clientHeight) return;

    const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;

    // Trigger loadMore when within 120px from bottom
    if (distanceFromBottom < 120 && !loadingMore) {
      loadMore();
    }
  };
  return (
    <>
      <SlideoverModal
        visible={visible}
        onClose={() => setVisible(false)}
        backdrop={false}
        headerTitle={`Pending Remarks Queue`}
        size="md"
        // headerRight={
        //   <p className="bg-[#D4183D] border border-[#A70A29] text-white rounded-full p-2">
        //     {list.length ?? 0}
        //   </p>
        // }
      >
        <div
          className="w-full h-[calc(100vh-120px)] overflow-y-auto"
          onScroll={handleScroll}
        >
          {getQueueCalls?.loading && list.length === 0 ? (
            <div className=" text-slate-600 w-10 h-10  flex items-center justify-center mx-auto mt-30">
              <LoadingIcon className=" " />
            </div>
          ) : (
            <div className="space-y-3">
              {list?.length > 0 &&
                list?.map((item) => {
                  const formatted = userTimeZone(item.event_time);

                  return (
                    <div
                      key={item?.session_id}
                      className="border-2 rounded-xl p-3 hover:bg-slate-50 cursor-pointer"
                    >
                      {item.direction === "Outbound" ? (
                        <div
                          onClick={() => {
                            setOpenPostCall(true);
                            setGetCallerNum(item.to_number);
                            setDirection("Outbound");
                            setGetSessionID(item?.session_id);
                          }}
                        >
                          <div className="">
                            <div className="flex justify-between items-center">
                              <div className="flex items-center gap-2">
                                <div className="rounded-full w-11 h-11 flex items-center justify-center bg-slate-100">
                                  <Lucide icon={"PhoneOutgoing"} />
                                </div>
                                <p className="font-medium text-slate-900">
                                  {`${authUser?.name} ${
                                    authUser?.last_name || ""
                                  }`}
                                </p>
                                <p className="text-[#0A0A0A] text-sm border border-[#0A0A0A10] py-0.5 px-2 rounded-lg">
                                  outgoing
                                </p>
                              </div>
                              <p className="text-[#717182]">
                                {formatted ? formatted.customFromNow : ""}
                              </p>
                            </div>
                            <div>
                              <div className="text-sm text-slate-500 py-2">
                                {item.to_number}
                              </div>
                              <div className="text-sm text-slate-500 ">
                                {formatted ? formatted.time12h : ""}
                              </div>
                              {/* <div className="text-sm text-slate-500">
                              {item.time}
                            </div> */}
                              {/* {item.queryId && (
                              <div className="mt-2 text-xs text-blue-600">
                                Linked to {item.queryId}
                              </div>
                            )} */}
                            </div>
                          </div>
                        </div>
                      ) : (
                        <div
                          className="flex-1"
                          onClick={() => {
                            setOpenPostCall(true);
                            setGetCallerNum(item.from_number);
                            setDirection("Inbound");
                            setGetSessionID(item?.session_id);
                          }}
                        >
                          <div className="">
                            <div className="flex justify-between items-center">
                              <div className="flex items-center gap-2">
                                <div className="rounded-full w-11 h-11 flex items-center justify-center bg-slate-100">
                                  <Lucide icon={"PhoneIncoming"} />
                                </div>
                                <p className="font-medium text-slate-900">
                                  {item?.agent_name ?? "New Customer"}
                                </p>
                                <p className="text-[#0A0A0A] text-sm border border-[#0A0A0A10] py-0.5 px-2 rounded-lg">
                                  incoming
                                </p>
                              </div>
                              <p className="text-[#717182]">
                                {formatted ? formatted.customFromNow : ""}
                              </p>
                            </div>
                            <div>
                              <div className="text-sm text-slate-500 py-2">
                                {item.from_number}
                              </div>
                              <div className="text-sm text-slate-500 ">
                                {formatted ? formatted.time12h : ""}
                              </div>
                              {/* <div className="text-sm text-slate-500">
                              {item.time}
                            </div> */}
                              {/* {item.queryId && (
                              <div className="mt-2 text-xs text-blue-600">
                                Linked to {item.queryId}
                              </div>
                            )} */}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              {loadingMore && (
                <div className="flex justify-center py-4">
                  <LoadingIcon />
                </div>
              )}
              {!pagination?.next_page && list.length > 0 && (
                <p className="text-center text-gray-500 py-2">
                  No more records
                </p>
              )}
            </div>
          )}
        </div>
      </SlideoverModal>

      <PostCallRemarks
        visible={openPostCall}
        onClose={handleClose}
        getCallerNum={getCallerNum}
        direction={direction}
        getSessionID={getSessionID}
      />
    </>
  );
};

export default PendingRemarksPanel;
